const mk_measurement = (() => {
    const DEBUG = false;

    const config = {
        mobileBreakpoint: 1199,
        defaultMin: 1,
        defaultMax: 9999,
        defaultText: 'x cm',
        urlParamPrefix: 'm_',
        textPadding: 8,
        error: {
            empty: 'Wert darf nicht leer oder 0 sein',
            min: (min) => `Wert muss mindestens ${min}cm sein`,
            max: (max) => `Wert darf maximal ${max}cm sein`,
            num: 'Bitte eine gültige Zahl eingeben',
            nan: 'Ungültige Zahl',
        },
    };

    const state = {
        root: null,
        mount: null,
        modal: null,
        scrollY: 0,
        steps: 0,
        activeInput: null
    };

    const qs = (selector, scope = document) => scope.querySelector(selector);
    const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

    const PREFIX = '🪡 [measurement]';
    const log = {
        info: (...args) => DEBUG && console.log(PREFIX, ...args),
        warn: (...args) => DEBUG && console.warn(PREFIX, ...args),
        error: (...args) => console.error(PREFIX, ...args),
        group: (label) => DEBUG && console.groupCollapsed(PREFIX + ' ' + label),
        groupEnd: () => DEBUG && console.groupEnd(),
    };

    const validate = function (value, min, max) {
        if (value === '') {
            return config.error.empty;
        }

        if (!/^(0|[1-9]\d*)$/.test(String(value))) {
            return config.error.num;
        }

        const num = Number(value);

        if (isNaN(num)) {
            return config.error.nan;
        }

        if (num < min) {
            return config.error.min(min);
        }

        if (num > max) {
            return config.error.max(max);
        }
        return true;
    };

    const openPopup = function (sourceInput) {
        state.scrollY = window.scrollY;
        state.sourceInput = sourceInput || null;
        document.body.style.position = "fixed";
        document.body.style.top = `-${state.scrollY}px`;
        document.body.style.left = `0px`;
        state.modal.showModal();
        qsa('text[data-id]', state.modal).forEach(fitText);

        let target = null;
        if (sourceInput && sourceInput.id) {
            target = document.getElementById(sourceInput.id + '-virtual');
        }
        if (!target) {
            target = qs('.measurements__modal-form input');
            if (sourceInput) {
                log.warn('openPopup: kein virtuelles Feld zu', sourceInput.id, '- fallback auf erstes Feld');
            }
        }
        if (target) {
            setActiveInput(target);
        } else {
            log.warn('openPopup: kein Zielfeld im Modal gefunden');
        }

        log.info('Popup geöffnet — Source:', sourceInput ? sourceInput.id : '(none)', '→ Target:', target ? target.id : '(none)');
    };

    const closePopup = function () {
        log.info('Popup geschlossen — Source war:', state.sourceInput ? state.sourceInput.id : '(none)');
        state.modal.close();
        if (state.sourceInput && state.sourceInput.blur) {
            state.sourceInput.blur();
        }
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        state.sourceInput = null;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        window.scrollTo(0, state.scrollY);
    };

    // Bullets in der Paint-Order ans Ende ihres Containers heben, damit ihr
    // weißer Konturrand (Halo, siehe CSS) die Pfeillinien sicher überdeckt.
    const raiseBullets = function (svgEl) {
        qsa('g[data-bullet]', svgEl).forEach(function (g) {
            g.parentNode.appendChild(g);
        });
    };

    const fitText = function (textEl) {
        const svg = textEl.ownerSVGElement;
        if (!svg || !svg.viewBox || !svg.viewBox.baseVal) {
            log.warn('fitText: kein gültiges ownerSVGElement/viewBox für', textEl.id);
            return;
        }

        const target = textEl.closest('g[data-bullet]') || textEl;
        textEl.removeAttribute('textLength');
        textEl.removeAttribute('lengthAdjust');
        if (target !== textEl) textEl.removeAttribute('transform');
        target.removeAttribute('transform');
        target.style.removeProperty('--mk-tx');
        target.style.removeProperty('--mk-ty');

        const svgRect = svg.getBoundingClientRect();
        const textRect = textEl.getBoundingClientRect();
        if (!svgRect.width || !svgRect.height || !textRect.width || !textRect.height) {
            log.warn('fitText: rect 0 für', textEl.id, '— evtl. unsichtbar/display:none');
            return;
        }

        const vb = svg.viewBox.baseVal;
        const sx = svgRect.width / vb.width;

        const tx = (textRect.left - svgRect.left) / sx + vb.x;
        const tw = textRect.width / sx;

        const pad = config.textPadding;
        const maxWidth = vb.x + vb.width - pad - tx;

        log.info('fitText', textEl.id,
            '| text x/w', tx.toFixed(1), tw.toFixed(1),
            '| maxWidth', maxWidth.toFixed(1));

        if (maxWidth <= 0) {
            log.warn('fitText', textEl.id, '→ kein Platz, lasse unverändert');
            return;
        }

        if (tw <= maxWidth) {
            return;
        }

        const naturalLength = textEl.getComputedTextLength();
        if (!naturalLength) return;
        const factor = maxWidth / tw;
        const newTextLength = naturalLength * factor;
        textEl.setAttribute('textLength', newTextLength.toFixed(2));
        textEl.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        log.info('fitText', textEl.id, '→ skaliere auf textLength', newTextLength.toFixed(1), '(factor', factor.toFixed(3) + ')');
    };

    const inputEventHandler = function () {
        const number = this.dataset.id;
        // const id = "xcm"+number;
        const id = this.id;
        const textElements = qsa('text[data-id="'+number+'"]');
        const textTargets = qsa('text[data-id="'+number+'"] tspan');
        const currentValue = this.value;
        // const errorSpan = qs('[data-id='+id+'] .mk-error');
        // TODO: quite fragile, need to have "xcm" in name

        if (id.includes('-virtual')) {    
            const originalId = id.replace('-virtual', '');
            document.getElementById(originalId).value = currentValue;
        } else {
            const virtualId = id + '-virtual';
            document.getElementById(virtualId).value = currentValue;
        }

        const validated = validate(currentValue, this.min, this.max);

        if (validated === true) {
            qsa('[data-id="'+number+'"]').forEach(function(element) {
                element.parentElement.classList.remove('error');
                element.parentElement.classList.add('success');
            });
            qsa('[data-arrow="'+number+'"]').forEach(function(arrow) {
                arrow.classList.add('scale');
            });
            qsa('[data-circle="'+number+'"]').forEach(function(circle) {
                circle.classList.add('is-valid');
            });
            textTargets.forEach(function(target) {
                target.textContent = currentValue + ' cm';
            });
            textElements.forEach(fitText);
        } else {
            qsa('[data-id="'+number+'"]').forEach(function(element) {
                element.parentElement.classList.add('error');
                element.parentElement.classList.remove('success');
            });
            qsa('[data-arrow="'+number+'"]').forEach(function(arrow) {
                arrow.classList.remove('scale');
            });
            qsa('[data-circle="'+number+'"]').forEach(function(circle) {
                circle.classList.remove('is-valid');
            });
            textTargets.forEach(function(target) {
                target.textContent = config.defaultText;
            });
            textElements.forEach(fitText);
        }
        
        updateProgress();
        syncToUrl();
    };

    const focusEventHandler = function (event) {
        // if swiper slider then use slideNext or similar,
        // otherwise use native scrollTo function
        if (qs('.product-media__slider').swiper !== null) {
            const slider = qs('.product-media__slider').swiper;
            const index = slider.slides.indexOf(state.mount);
            slider.slideTo(index);
        } else {
            // TODO: What is target???
            state.mount.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const wheelEventHandler = function (event) {
        event.preventDefault();
    };

    const clickEventHandler = function (event) {
        const id = this.id;
        // Only if not virtual inputs
        if (!id.includes("-virtual") && window.innerWidth <= config.mobileBreakpoint) {
            openPopup(this);
        }
    };

    const preventEnter = function (event) {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const beforeInputHandler = function (event) {
        if (event.data == null) return;
        if (!/^\d+$/.test(event.data)) {
            event.preventDefault();
        }
    };

    // Scrollt das aktive Feld innerhalb der scrollbaren Felderliste sichtbar,
    // ohne andere Scroll-Container (Body, Modal) zu bewegen.
    const scrollFieldIntoView = function (field) {
        const container = field.closest('.measurements__modal-form');
        if (!container || container.scrollHeight <= container.clientHeight) return;
        const cRect = container.getBoundingClientRect();
        const fRect = field.getBoundingClientRect();
        const margin = 8;
        if (fRect.top < cRect.top + margin) {
            container.scrollBy({ top: fRect.top - cRect.top - margin, behavior: 'smooth' });
        } else if (fRect.bottom > cRect.bottom - margin) {
            container.scrollBy({ top: fRect.bottom - cRect.bottom + margin, behavior: 'smooth' });
        }
    };

    const setActiveInput = function (input) {
        state.activeInput = input;
        qsa('.measurements__modal-form .measurements__field').forEach(f => f.classList.remove('is-active'));
        const field = input.closest('.measurements__field');
        if (field) field.classList.add('is-active');

        if (document.activeElement !== input) {
            input.focus({ preventScroll: true });
        }
        const len = input.value.length;
        try { input.setSelectionRange(len, len); } catch (e) {}

        if (field) scrollFieldIntoView(field);
    };

    const virtualFocusHandler = function () {
        setActiveInput(this);
    };

    const keyPressStartHandler = function (event) {
        event.preventDefault();
        this.classList.add('is-pressed');
        this.dataset.pressedAt = Date.now();
    };

    const keyPressEndHandler = function () {
        const minDuration = 120;
        const elapsed = Date.now() - Number(this.dataset.pressedAt || 0);
        const remaining = Math.max(0, minDuration - elapsed);
        setTimeout(() => this.classList.remove('is-pressed'), remaining);
    };

    const keyClickHandler = function (event) {
        event.preventDefault();

        let input = state.activeInput;
        if (!input || !input.isConnected) {
            input = qs('.measurements__modal-form input');
            if (!input) return;
            setActiveInput(input);
        }

        const key = this.dataset.key;
        let value = input.value;

        if (key === 'del') {
            value = value.slice(0, -1);
        } else {
            if (value === '0') {
                value = key;
            } else {
                value = value + key;
            }
        }

        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const getFieldKey = function (input) {
        return input.id.replace('measurements__field-', '').replace('-virtual', '');
    };

    const syncToUrl = function () {
        try {
            const url = new URL(window.location.href);
            qsa('.measurements__form-original input').forEach(function (input) {
                const param = config.urlParamPrefix + getFieldKey(input);
                if (input.value !== '') {
                    url.searchParams.set(param, input.value);
                } else {
                    url.searchParams.delete(param);
                }
            });
            history.replaceState(null, '', url.toString());
        } catch (e) {
            log.error('URL-Sync fehlgeschlagen:', e);
        }
    };

    const restoreFromUrl = function () {
        const url = new URL(window.location.href);
        const restored = {};
        qsa('.measurements__form-original input').forEach(function (input) {
            const param = config.urlParamPrefix + getFieldKey(input);
            const raw = url.searchParams.get(param);
            if (raw === null || raw === '') return;
            const num = Number(String(raw).replace(',', '.'));
            if (!Number.isFinite(num)) return;
            const value = String(Math.round(num));
            input.value = value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            restored[param] = value;
        });
        const count = Object.keys(restored).length;
        if (count > 0) {
            log.info('Werte aus URL wiederhergestellt (' + count + '):', restored);
        } else {
            log.info('Keine URL-Parameter zum Wiederherstellen');
        }
    };

    const updateProgress = function () {
        let filledCount = 0;
        const requiredFields = qsa('.measurements__form-original input[required]');

        requiredFields.forEach(field => {
            if (validate(field.value, field.min, field.max) === true) {
                filledCount++;
            }
        });
        const progressPercent = (filledCount / requiredFields.length) * 100;
        qs('.measurements__progress-bar').style.width = `${progressPercent}%`;
        qs('.measurements__progress-steps').textContent = filledCount + '/' + requiredFields.length;

        if (progressPercent === 100) {
            state.root.classList.add('done');
        } else {
            state.root.classList.remove('done');
        }
    };

    const init = function () {
        log.group('Init');

        state.root = qs('.measurements');
        state.mount = qs('.measurements__mount');
        state.modal = qs('.measurements__modal');

        if (!state.mount || !state.modal) {
            log.error('Init abgebrochen — fehlend:',
                !state.mount ? '.measurements__mount' : null,
                !state.modal ? '.measurements__modal' : null
            );
            log.groupEnd();
            return;
        }

        const originalCount = qsa('.measurements__form-original input').length;
        const virtualCount = qsa('.measurements__modal-form input').length;
        const keyCount = qsa('.measurements__keyboard .key').length;
        log.info('DOM gefunden — Original-Inputs:', originalCount, '| Virtual-Inputs:', virtualCount, '| Tastatur-Keys:', keyCount);

        if (originalCount !== virtualCount) {
            log.warn('Mismatch: Anzahl Original- und Virtual-Inputs unterschiedlich');
        }

        const svgDataElement = qs('[data-svg]', state.mount);

        if (!svgDataElement) {
            log.error('SVG-Quelle fehlt — kein [data-svg] in .measurements__mount');
            log.groupEnd();
            return;
        }

        const svgUrl = svgDataElement.dataset.svg;
        log.info('Lade Haupt-SVG:', svgUrl);

        fetch(svgUrl)
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status} ${r.statusText}`);
                return r.text();
            })
            .then(svg => {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
                const svgEl = svgDoc.documentElement;

                svgEl.classList.add('product-media__image');

                raiseBullets(svgEl);

                qs('picture', state.mount).replaceWith(svgEl);
                log.info('Haupt-SVG ins DOM eingehängt');

                const svgMount = qs('.measurements__modal-svg-mount');
                if (svgMount) {
                    const svgElClone = svgEl.cloneNode(true);
                    svgMount.replaceWith(svgElClone);
                    log.info('Haupt-SVG ins Modal geklont');
                } else {
                    log.warn('.measurements__modal-svg-mount nicht gefunden');
                }

                qsa('.measurements__field input').forEach(function(field) {
                    field.addEventListener('keydown', preventEnter);
                    field.addEventListener('beforeinput', beforeInputHandler);
                    field.addEventListener('focus', focusEventHandler);
                    field.addEventListener('wheel', wheelEventHandler, { passive: false });
                    field.addEventListener('click', clickEventHandler);
                    field.addEventListener('input', inputEventHandler);
                });
                log.info('Input-Listener verdrahtet auf', qsa('.measurements__field input').length, 'Feldern');
            })
            .then(() => {
                qsa('text[data-id]').forEach(fitText);
                restoreFromUrl();
                log.groupEnd();
            })
            .catch(err => {
                log.error('Haupt-SVG-Load fehlgeschlagen (' + svgUrl + '):', err.message || err);
                log.groupEnd();
            });
        
        // Click event for modal buttons
        const closeBtn = qs('.measurements__modal-close');
        const saveBtn = qs('.measurements__modal-save-button');
        if (closeBtn) closeBtn.addEventListener('click', closePopup);
        else log.warn('.measurements__modal-close nicht gefunden');
        if (saveBtn) saveBtn.addEventListener('click', closePopup);
        else log.warn('.measurements__modal-save-button nicht gefunden');

        // Aktives virtuelles Input tracken
        qsa('.measurements__modal-form input').forEach(function (input) {
            input.addEventListener('focus', virtualFocusHandler);
            input.addEventListener('click', virtualFocusHandler);
        });

        // Eigene Tastatur
        qsa('.measurements__keyboard .key').forEach(function (key) {
            key.addEventListener('pointerdown', keyPressStartHandler);
            key.addEventListener('pointerup', keyPressEndHandler);
            key.addEventListener('pointercancel', keyPressEndHandler);
            key.addEventListener('pointerleave', keyPressEndHandler);
            key.addEventListener('click', keyClickHandler);
        });
        
        // Check on submit for invalid — capture phase + stopImmediatePropagation,
        // sonst feuert der theme-eigene Submit-Listener trotzdem den Cart-Add via fetch.
        const submitGuard = function (event) {
            const fields = qsa('.measurements__form-original input[required]');
            const failures = [];
            let firstInvalid = null;

            fields.forEach(function (field) {
                const result = validate(field.value, field.min, field.max);
                if (result === true) {
                    field.parentElement.classList.remove('error');
                    field.parentElement.classList.add('success');
                } else {
                    field.parentElement.classList.add('error');
                    field.parentElement.classList.remove('success');
                    failures.push({ key: getFieldKey(field), value: field.value, reason: result });
                    if (!firstInvalid) firstInvalid = field;
                }
            });

            if (firstInvalid) {
                event.preventDefault();
                event.stopImmediatePropagation();
                firstInvalid.focus();
                firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                log.warn('Submit blockiert — ' + failures.length + ' ungültige(s) Feld(er):', failures);
            } else {
                log.info('Submit OK — alle ' + fields.length + ' Pflichtfelder valide');
            }
        };

        const productForms = qsa('.product-form');
        if (productForms.length === 0) {
            log.warn('Keine .product-form gefunden — Submit-Guard nicht aktiv');
        } else {
            productForms.forEach(function (form) {
                form.addEventListener('submit', submitGuard, true);
            });
            log.info('Submit-Guard auf', productForms.length, '.product-form(s) registriert');
        }
    };

    return { init };
})();

mk_measurement.init();